<template>
  <div class="reserve-catalog-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('reserveCatalogManage.list.title')" :subtitle="$t('reserveCatalogManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.name" clearable
            :placeholder="$t('reserveCatalogManage.search.namePlaceholder')"
            @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.type" clearable class="w-full"
            :placeholder="$t('reserveCatalogManage.search.typePlaceholder')">
            <el-option :label="$t('reserveCatalogManage.search.typeOptions.all')" value="" />
            <el-option :label="$t('reserveCatalogManage.search.typeOptions.dining')" value="1001" />
            <el-option :label="$t('reserveCatalogManage.search.typeOptions.service')" value="2002" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('reserveCatalogManage.list.title') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAdd">
            {{ $t('common.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('reserveCatalogManage.page.empty')" @retry="getList">
        <el-table-column prop="type" :label="$t('reserveCatalogManage.table.type')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.type === '1001'
              ? $t('reserveCatalogManage.table.typeOptions.dining')
              : $t('reserveCatalogManage.table.typeOptions.service')"
              :text="scope.row.type === '1001'
                ? $t('reserveCatalogManage.table.typeOptions.dining')
                : $t('reserveCatalogManage.table.typeOptions.service')" type="info" />
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('reserveCatalogManage.table.name')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="sort" :label="$t('reserveCatalogManage.table.sort')" align="center" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.sort }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="state" :label="$t('reserveCatalogManage.table.state')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.state === '1001'
              ? $t('reserveCatalogManage.table.stateOptions.show')
              : $t('reserveCatalogManage.table.stateOptions.hide')"
              :text="scope.row.state === '1001'
                ? $t('reserveCatalogManage.table.stateOptions.show')
                : $t('reserveCatalogManage.table.stateOptions.hide')"
              :type="scope.row.state === '1001' ? 'success' : 'info'" />
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('reserveCatalogManage.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">{{ $t('common.edit') }}</el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">{{ $t('common.delete') }}</el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 子组件 -->
    <add-reserve-catalog ref="addReserveCatalog" @success="handleSuccess" />
    <edit-reserve-catalog ref="editReserveCatalog" @success="handleSuccess" />
    <delete-reserve-catalog ref="deleteReserveCatalog" @success="handleSuccess" />
  </div>
</template>

<script>
import { listReserveCatalog } from '@/api/scm/reserveCatalogManageApi'
import AddReserveCatalog from '@/components/scm/addReserveCatalog'
import EditReserveCatalog from '@/components/scm/editReserveCatalog'
import DeleteReserveCatalog from '@/components/scm/deleteReserveCatalog'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ReserveCatalogManageList',
  components: {
    AddReserveCatalog,
    EditReserveCatalog,
    DeleteReserveCatalog,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        name: '',
        type: '',
        communityId: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.searchForm.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const { data, total } = await listReserveCatalog(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('查询预约目录失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('reserveCatalogManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.name = ''
      this.searchForm.type = ''
      this.page.current = 1
      this.getList()
    },
    handleAdd() {
      this.$refs.addReserveCatalog.open()
    },
    handleEdit(row) {
      this.$refs.editReserveCatalog.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteReserveCatalog.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.reserve-catalog-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
